/**
 * Produces a compact text summary of what's visible on the page right
 * now. The summary goes to Claude alongside a screenshot, and each
 * interactive line starts with a `[xN]` ref that Claude can reference
 * in its `[POINT:xN:label]` response tag.
 */

import type { TaggedElement } from "./InteractiveElementTagger";

const MAX_BODY_TEXT_CHARS = 4000;
const MAX_HEADINGS = 25;

export interface DOMSerializationResult {
  /** Full text block passed to Claude. */
  summary: string;
}

export function serializePageContext(
  taggedElements: readonly TaggedElement[],
): DOMSerializationResult {
  const lines: string[] = [];

  lines.push(`URL: ${window.location.href}`);
  lines.push(`Title: ${document.title}`);
  lines.push(
    `Viewport: ${window.innerWidth}x${window.innerHeight} ` +
      `scrollY=${Math.round(window.scrollY)} ` +
      `docHeight=${document.documentElement.scrollHeight}`,
  );

  const headings = collectHeadings();
  if (headings.length > 0) {
    lines.push("");
    lines.push("Headings:");
    headings.slice(0, MAX_HEADINGS).forEach((heading) => {
      lines.push(`  ${heading.level} ${heading.text}`);
    });
  }

  if (taggedElements.length > 0) {
    lines.push("");
    lines.push(
      "Interactive elements (reference by [ref] when pointing):",
    );
    taggedElements.forEach((tagged) => {
      const rectNote = describeOffscreenHint(tagged.element);
      const labelPart = tagged.label ? ` "${tagged.label}"` : "";
      const detailPart = tagged.detail ? ` ${tagged.detail}` : "";
      lines.push(
        `  [${tagged.ref}] <${tagged.role}>${labelPart}${detailPart}${rectNote}`,
      );
    });
  }

  const bodyText = collectVisibleBodyText();
  if (bodyText.length > 0) {
    lines.push("");
    lines.push("Visible text:");
    lines.push(bodyText);
  }

  return { summary: lines.join("\n") };
}

function collectHeadings(): { level: string; text: string }[] {
  const headings: { level: string; text: string }[] = [];
  const nodes = document.querySelectorAll<HTMLElement>("h1, h2, h3");
  nodes.forEach((node) => {
    const text = collapseWhitespace(node.textContent ?? "");
    if (text.length === 0) return;
    headings.push({ level: node.tagName.toLowerCase(), text });
  });
  return headings;
}

function collectVisibleBodyText(): string {
  const text = document.body.innerText || "";
  const collapsed = collapseWhitespace(text);
  if (collapsed.length <= MAX_BODY_TEXT_CHARS) {
    return collapsed;
  }
  return collapsed.slice(0, MAX_BODY_TEXT_CHARS) + "…";
}

function describeOffscreenHint(element: HTMLElement): string {
  const rect = element.getBoundingClientRect();
  const viewportHeight = window.innerHeight;
  if (rect.top < 0) return " [above-viewport]";
  if (rect.bottom > viewportHeight) return " [below-viewport]";
  return "";
}

function collapseWhitespace(text: string): string {
  return text.replace(/[ \t\u00a0]+/g, " ").replace(/\n{3,}/g, "\n\n").trim();
}
